import { toneVariant, useCompanionNoun } from '@/utils/personalization'
import PageLayout from '@app-studio-web/ui/components/PageLayout'
import ProgressBar from '@app-studio-web/ui/components/ProgressBar'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

const steps = [
  'pages.onboarding.analyzing.step1',
  'pages.onboarding.analyzing.step2',
  'pages.onboarding.analyzing.step3',
  'pages.onboarding.analyzing.step4',
  'pages.onboarding.analyzing.step5',
  'pages.onboarding.analyzing.step6',
]

const STEP_INTERVAL = 650
const PROGRESS_TIME = steps.length * STEP_INTERVAL + 1000

export default function AnalyzingPage() {
  const { t } = useTranslation()
  const noun = useCompanionNoun()
  const submit = useSubmit()
  const [activeStep, setActiveStep] = useState(0)
  const stepRef = useRef(0)

  useEffect(() => {
    const interval = setInterval(() => {
      if (stepRef.current >= steps.length) {
        clearInterval(interval)
        const form = document.getElementById(
          'analyzing-form',
        ) as HTMLFormElement | null
        if (form) submit(form)
        return
      }
      stepRef.current += 1
      setActiveStep(stepRef.current)
    }, STEP_INTERVAL)

    return () => clearInterval(interval)
  }, [submit])

  return (
    <PageLayout>
      <div className="flex grow flex-col items-center justify-center gap-6 text-center">
        <div className="flex gap-1.5 rounded-2xl rounded-bl-md bg-white px-5 py-4 shadow-[0_2px_14px_rgba(43,34,48,0.08)]">
          {[0, 0.2, 0.4].map((delay) => (
            <span
              key={delay}
              className="h-2.5 w-2.5 rounded-full bg-[#4A4A4A]"
              style={{
                animation: `typingBounce 1.4s ease-in-out ${delay}s infinite`,
              }}
            />
          ))}
        </div>
        <h2 className="text-primary font-serif text-3xl font-normal">
          {t(toneVariant('pages.onboarding.analyzing.title'), { noun })}
        </h2>
        <div className="w-full max-w-[420px]">
          <ProgressBar
            progressTime={PROGRESS_TIME}
            subtitle={
              activeStep < steps.length
                ? t(steps[activeStep], { noun })
                : t(steps[steps.length - 1], { noun })
            }
          />
        </div>
        <ul className="w-full max-w-[420px] space-y-2 text-left">
          {steps.map((step, idx) => {
            const done = idx < activeStep
            const current = idx === activeStep
            return (
              <li
                key={step}
                className={`flex items-center gap-3 rounded-xl px-4 py-2.5 transition-all duration-300 ${
                  done ? 'bg-white/90' : current ? 'bg-white/80' : 'bg-white/40'
                }`}
              >
                <span
                  className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs transition-colors duration-300 ${
                    done
                      ? 'bg-emerald-500 text-white'
                      : current
                        ? 'bg-primary text-white'
                        : 'text-muted bg-white/60'
                  }`}
                >
                  {done ? '✓' : current ? '…' : idx + 1}
                </span>
                <span
                  className={`text-sm transition-colors duration-300 ${
                    done
                      ? 'text-muted line-through'
                      : current
                        ? 'text-primary font-medium'
                        : 'text-muted/60'
                  }`}
                >
                  {t(step, { noun })}
                </span>
              </li>
            )
          })}
        </ul>
      </div>
      <Form id="analyzing-form" method="post">
        <input type="hidden" name="analyzing" value="done" />
      </Form>
    </PageLayout>
  )
}
